import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { exportBudget } from '@/utils/exportUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CSI_CATEGORIES = [
  '01 - General Requirements', '02 - Existing Conditions', '03 - Concrete', '04 - Masonry',
  '05 - Metals', '06 - Wood, Plastics, Composites', '07 - Thermal & Moisture Protection',
  '08 - Openings', '09 - Finishes', '10 - Specialties', '11 - Equipment', '12 - Furnishings',
  '13 - Special Construction', '14 - Conveying Equipment', '21 - Fire Suppression',
  '22 - Plumbing', '23 - HVAC', '26 - Electrical', '27 - Communications', '28 - Electronic Safety',
  '31 - Earthwork', '32 - Exterior Improvements', '33 - Utilities', 'Others'
];

export const BudgetTabEnhanced = ({ projectId }) => {
  const [budgets, setBudgets] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ category: '', contractor_name: '', budget_amount: '', actual_spend: '', approved_change_orders: '', comments: '' });

  useEffect(() => {
    fetchBudgets();
  }, [projectId]);

  const fetchBudgets = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/budget`);
      setBudgets(response.data);
    } catch (error) {
      toast.error('Failed to load budgets');
    }
  };

  const createOrUpdateBudget = async (e) => {
    e.preventDefault();
    try {
      const data = {
        category: formData.category,
        contractor_name: formData.contractor_name,
        budget_amount: parseFloat(formData.budget_amount),
        actual_spend: parseFloat(formData.actual_spend),
        approved_change_orders: parseFloat(formData.approved_change_orders),
        comments: formData.comments
      };
      if (editingId) {
        await axios.patch(`${API}/budget/${editingId}`, data);
        toast.success('Budget updated!');
      } else {
        await axios.post(`${API}/projects/${projectId}/budget`, data);
        toast.success('Budget entry created!');
      }
      setShowCreateModal(false);
      setEditingId(null);
      setFormData({ category: '', contractor_name: '', budget_amount: '', actual_spend: '', approved_change_orders: '', comments: '' });
      fetchBudgets();
    } catch (error) {
      toast.error(editingId ? 'Failed to update budget' : 'Failed to create budget entry');
    }
  };

  const editBudget = (budget) => {
    setFormData({
      category: budget.category || '',
      contractor_name: budget.contractor_name,
      budget_amount: budget.budget_amount.toString(),
      actual_spend: budget.actual_spend.toString(),
      approved_change_orders: budget.approved_change_orders.toString(),
      comments: budget.comments || ''
    });
    setEditingId(budget.id);
    setShowCreateModal(true);
  };

  const deleteBudget = async (budgetId) => {
    if (!window.confirm('Delete this budget entry?')) return;
    try {
      await axios.delete(`${API}/budget/${budgetId}`);
      toast.success('Budget deleted');
      fetchBudgets();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const calculateTotal = (budget) => budget.budget_amount + budget.approved_change_orders;
  const calculateVariance = (budget) => calculateTotal(budget) - budget.actual_spend;

  const totals = budgets.reduce((acc, b) => ({
    budget: acc.budget + b.budget_amount,
    actual: acc.actual + b.actual_spend,
    changeOrders: acc.changeOrders + b.approved_change_orders
  }), { budget: 0, actual: 0, changeOrders: 0 });

  return (
    <div className="space-y-4" data-testid="budget-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Project Budget</h2>
          <p className="text-sm text-slate-600 mt-1">Track costs by category and contractor</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => exportBudget(budgets, 'excel')} className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 text-sm" disabled={budgets.length === 0}>Export Excel</button>
          <button onClick={() => exportBudget(budgets, 'pdf')} className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 text-sm" disabled={budgets.length === 0}>Export PDF</button>
          <button onClick={() => { setEditingId(null); setFormData({ category: '', contractor_name: '', budget_amount: '', actual_spend: '', approved_change_orders: '', comments: '' }); setShowCreateModal(true); }} className="bg-slate-900 text-white px-4 py-2 rounded-md hover:bg-slate-800" data-testid="create-budget-button">Add Budget Line</button>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-auto">
        <table className="w-full" data-testid="budget-table">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Category</th>
              <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Contractor</th>
              <th className="text-right p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Original Budget</th>
              <th className="text-right p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Change Orders</th>
              <th className="text-right p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Revised Budget</th>
              <th className="text-right p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Actual Spend</th>
              <th className="text-right p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Variance</th>
              <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Comments</th>
              <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Actions</th>
            </tr>
          </thead>
          <tbody>
            {budgets.map((budget) => {
              const revisedBudget = calculateTotal(budget);
              const variance = calculateVariance(budget);
              return (
                <tr key={budget.id} className="border-b hover:bg-slate-50" data-testid={`budget-row-${budget.id}`}>
                  <td className="p-4 text-sm font-medium">{budget.category || 'N/A'}</td>
                  <td className="p-4">{budget.contractor_name}</td>
                  <td className="p-4 font-mono text-right">${budget.budget_amount.toLocaleString()}</td>
                  <td className="p-4 font-mono text-right">${budget.approved_change_orders.toLocaleString()}</td>
                  <td className="p-4 font-mono text-right font-semibold">${revisedBudget.toLocaleString()}</td>
                  <td className="p-4 font-mono text-right">${budget.actual_spend.toLocaleString()}</td>
                  <td className={`p-4 font-mono text-right font-semibold ${variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {variance >= 0 ? '+' : ''}${variance.toLocaleString()}
                  </td>
                  <td className="p-4 text-sm text-slate-600">{budget.comments || '-'}</td>
                  <td className="p-4">
                    <div className="flex gap-2">
                      <button onClick={() => editBudget(budget)} className="text-sm text-blue-600 hover:underline">Edit</button>
                      <button onClick={() => deleteBudget(budget.id)} className="text-sm text-red-600 hover:underline">Delete</button>
                    </div>
                  </td>
                </tr>
              );
            })}
            <tr className="bg-slate-100 font-bold">
              <td className="p-4" colSpan="2">TOTALS</td>
              <td className="p-4 font-mono text-right">${totals.budget.toLocaleString()}</td>
              <td className="p-4 font-mono text-right">${totals.changeOrders.toLocaleString()}</td>
              <td className="p-4 font-mono text-right">${(totals.budget + totals.changeOrders).toLocaleString()}</td>
              <td className="p-4 font-mono text-right">${totals.actual.toLocaleString()}</td>
              <td className={`p-4 font-mono text-right ${(totals.budget + totals.changeOrders - totals.actual) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {(totals.budget + totals.changeOrders - totals.actual) >= 0 ? '+' : ''}${(totals.budget + totals.changeOrders - totals.actual).toLocaleString()}
              </td>
              <td className="p-4" colSpan="2"></td>
            </tr>
          </tbody>
        </table>
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowCreateModal(false)}>
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl" onClick={(e) => e.stopPropagation()} data-testid="create-budget-modal">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">{editingId ? 'Edit Budget Entry' : 'Add Budget Entry'}</h2>
            <form onSubmit={createOrUpdateBudget} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Category (CSI Division)</label>
                  <select value={formData.category} onChange={(e) => setFormData({...formData, category: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-md">
                    <option value="">Select category</option>
                    {CSI_CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Contractor Name</label>
                  <input type="text" value={formData.contractor_name} onChange={(e) => setFormData({...formData, contractor_name: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-md" required data-testid="budget-contractor-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Original Budget Amount</label>
                  <input type="number" step="0.01" value={formData.budget_amount} onChange={(e) => setFormData({...formData, budget_amount: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-md" required data-testid="budget-amount-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Actual Spend</label>
                  <input type="number" step="0.01" value={formData.actual_spend} onChange={(e) => setFormData({...formData, actual_spend: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-md" required data-testid="budget-spend-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Approved Change Orders (Auto-updated)</label>
                  <input type="number" step="0.01" value={formData.approved_change_orders} onChange={(e) => setFormData({...formData, approved_change_orders: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-md bg-slate-50" required data-testid="budget-changeorders-input" />
                  <p className="text-xs text-slate-500 mt-1">Updates automatically when COs are approved</p>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">Comments</label>
                  <textarea value={formData.comments} onChange={(e) => setFormData({...formData, comments: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-md" rows="2" data-testid="budget-comments-input" />
                </div>
              </div>
              <div className="flex gap-3">
                <button type="submit" className="flex-1 bg-slate-900 text-white py-2 rounded-md hover:bg-slate-800" data-testid="submit-budget-button">{editingId ? 'Update' : 'Add'}</button>
                <button type="button" onClick={() => { setShowCreateModal(false); setEditingId(null); }} className="flex-1 border border-slate-200 py-2 rounded-md hover:bg-slate-50" data-testid="cancel-budget-button">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};